import { Form } from 'factorial-form'
import { makeObservable, observable } from 'mobx'

import Allowance, {
  DEFAULT_CYCLE_LENGTH,
  DEFAULT_TIMEOFF_CYCLE,
  EMPLOYEE_HIRED_DATE,
} from 'modules/timeoff/store/models/Allowance'

class AllowanceForm extends Form {
  saved = false
  flexibleCyclesEnabled = false

  constructor(values: Record<string, unknown>, flexibleCyclesEnabled = false) {
    const allowanceFrequency = flexibleCyclesEnabled
      ? Allowance.FREQUENCIES.monthly_flexible
      : Allowance.FREQUENCIES.yearly
    const allowanceCycleLength = flexibleCyclesEnabled ? DEFAULT_CYCLE_LENGTH : null
    const allowanceCycleStart = flexibleCyclesEnabled ? Allowance.CYCLE_START.jan : null
    const allowanceTimeoffCycle = flexibleCyclesEnabled ? null : DEFAULT_TIMEOFF_CYCLE

    const defaultValues = {
      leave_type_ids: [],
      holiday_allowance_in_cents: 0,
      allowance_type: Allowance.ALLOWANCE_TYPES.days,
      days_type: Allowance.DAYS_TYPE.working_days,
      range_type: Allowance.RANGE_OPTIONS.extra_non_working_days_at_end,
      proration_type: Allowance.PRORATION_TYPE.proration_enabled,
      negative_counter_type: Allowance.NEGATIVE_COUNTER_TYPE.negative_counter_disabled,
      unlimited_holidays: false,
      carry_over_units_in_cents: 0,
      expire_in_months: 12,
      unlimited_carry_over: false,
      unlimited_carry_over_expiration: false,
      available_days: Allowance.AVAILABLE_OPTIONS_DAYS.all_days,
      timeoff_cycle: allowanceTimeoffCycle,
      rounding: Allowance.DEFAULT_ROUNDING_OPTION_DAYS,
      tenure_periods_enabled: false,
      tenure_period_transition: Allowance.TENURE_PERIOD_TRANSITION.beginning_of_cycle,
      tenure_periods: [],
      accrued_units_availability: Allowance.ACCRUED_UNITS_AVAILABILITY.current_cycle,
      source_units: Allowance.SOURCE_UNITS.base_units,
      frequency: allowanceFrequency,
      cycle_start: allowanceCycleStart,
      cycle_length: allowanceCycleLength,
      maximum_amount_in_cents: null,
      accrued_denominator_in_cents: null,
      accrued_factor_in_cents: null,
      pto_proratio_enabled: false,
      unlimited_accrued_hours: false,
      count_holiday_as_workable: false,
      count_sm_rest_days: false,
    }
    super(
      { ...defaultValues, ...values },
      {
        name: 'string',
        timeoff_policy_id: 'number',
        leave_type_ids: 'any',
        holiday_allowance_in_cents: 'cents',
        allowance_type: 'string',
        days_type: 'string',
        range_type: 'string',
        proration_type: 'string',
        negative_counter_type: 'string',
        unlimited_holidays: 'boolean',
        carry_over_units_in_cents: 'cents',
        expire_in_months: 'number',
        unlimited_carry_over: 'boolean',
        unlimited_carry_over_expiration: 'boolean',
        available_days: 'string',
        timeoff_cycle: 'string',
        rounding: 'string',
        tenure_periods_enabled: 'boolean',
        tenure_period_transition: 'string',
        tenure_periods: 'any',
        accrued_units_availability: 'any',
        source_units: 'any',
        toggle_source_units: 'boolean',
        frequency: 'string',
        cycle_start: 'string',
        cycle_length: 'number',
        maximum_amount_in_cents: 'cents',
        accrued_denominator_in_cents: 'cents',
        accrued_factor_in_cents: 'cents',
        pto_proratio_enabled: 'boolean',
        unlimited_accrued_hours: 'boolean',
        count_holiday_as_workable: 'boolean',
        count_sm_rest_days: 'boolean',
        limit_negative_counter_type: 'string',
      }
    )

    this.flexibleCyclesEnabled = flexibleCyclesEnabled
    makeObservable(this, {
      saved: observable,
    })
  }

  get isHourly(): boolean {
    return this.get('allowance_type').value === Allowance.ALLOWANCE_TYPES.hours
  }

  get isOuvrables(): boolean {
    return this.get('days_type').value === Allowance.DAYS_TYPE.french_calendar_days
  }

  get isNaturalDays(): boolean {
    return this.get('days_type').value === Allowance.DAYS_TYPE.natural_days
  }

  get isOvertime(): boolean {
    return this.get('source_units').value === Allowance.SOURCE_UNITS.overtime_units
  }

  get isTimeWorked(): boolean {
    return this.get('source_units').value === Allowance.SOURCE_UNITS.by_worked_time
  }

  get allowanceCycleFrequency() {
    return this.flexibleCyclesEnabled
      ? Allowance.FREQUENCIES.monthly_flexible
      : Allowance.FREQUENCIES.yearly
  }

  get allowanceCycleLength() {
    return this.flexibleCyclesEnabled ? DEFAULT_CYCLE_LENGTH : null
  }

  get allowanceCycleStart() {
    return this.flexibleCyclesEnabled ? Allowance.CYCLE_START.jan : null
  }

  get allowanceTimeoffCycle() {
    return this.flexibleCyclesEnabled ? null : DEFAULT_TIMEOFF_CYCLE
  }

  get maxBalanceCap() {
    return this.get('maximum_amount_in_cents').value
  }

  resetRounding(): void {
    if (this.isHourly) {
      if (!Object.keys(Allowance.ROUNDING_OPTIONS_HOURS).includes(this.get('rounding').value)) {
        this.get('rounding').set(Allowance.DEFAULT_ROUNDING_OPTION_HOURS)
      }
    } else {
      if (!Object.keys(Allowance.ROUNDING_OPTIONS_DAYS).includes(this.get('rounding').value)) {
        this.get('rounding').set(Allowance.DEFAULT_ROUNDING_OPTION_DAYS)
      }
    }
  }

  defaultOvertimeValue(overtimeLeaveTypeId: number) {
    this.get('source_units').set(Allowance.SOURCE_UNITS.overtime_units)
    this.get('leave_type_ids').set([overtimeLeaveTypeId])
    this.get('allowance_type').set(Allowance.ALLOWANCE_TYPES.hours)
    this.get('cycle_length').set(DEFAULT_CYCLE_LENGTH)
    this.get('cycle_start').set(Allowance.CYCLE_START.employee_hired_date)
    this.get('timeoff_cycle').set(EMPLOYEE_HIRED_DATE)
    this.get('frequency').set(Allowance.FREQUENCIES.lifetime)
    this.get('proration_type').set(Allowance.PRORATION_TYPE.proration_disabled)
  }

  defaultTimeWorkedValues() {
    this.get('leave_type_ids').set([])
    this.get('source_units').set(Allowance.SOURCE_UNITS.by_worked_time)
    this.get('allowance_type').set(Allowance.ALLOWANCE_TYPES.hours)
    this.get('frequency').set(this.allowanceCycleFrequency)
    this.get('cycle_length').set(this.allowanceCycleLength)
    this.get('cycle_start').set(this.allowanceCycleStart)
    this.get('timeoff_cycle').set(this.allowanceTimeoffCycle)
    this.get('rounding').set(Allowance.ROUNDING_OPTIONS_HOURS.decimals)
  }

  defaultFixedBalanceValues() {
    this.get('leave_type_ids').set([])
    this.get('source_units').set(Allowance.SOURCE_UNITS.base_units)
    this.get('allowance_type').set(Allowance.ALLOWANCE_TYPES.days)
    this.get('frequency').set(this.allowanceCycleFrequency)
    this.get('cycle_length').set(this.allowanceCycleLength)
    this.get('cycle_start').set(this.allowanceCycleStart)
    this.get('timeoff_cycle').set(this.allowanceTimeoffCycle)
  }

  toggleSourceUnitsOptions(overtimeLeaveTypeId: number): void {
    if (this.isOvertime) {
      return this.defaultOvertimeValue(overtimeLeaveTypeId)
    }

    if (this.isTimeWorked) {
      return this.defaultTimeWorkedValues()
    }

    return this.defaultFixedBalanceValues()
  }

  get isNegativeCounterEnabled(): boolean {
    return (
      this.get('negative_counter_type').value ===
      Allowance.NEGATIVE_COUNTER_TYPE.negative_counter_enabled
    )
  }
}

export default AllowanceForm
